// src/screens/AuthScreen.js
import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { Button, Note, ScreenHeader, Stack, TextInput } from "../components/ui";

// Länge des Login-Codes aus der Supabase-E-Mail
const CODE_LENGTH = 8;

export default function AuthScreen({ onAuthSuccess }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  // { text, tone }: tone "info" | "error" | "success" (siehe Note)
  const [message, setMessage] = useState(null);

  async function handleSendCode() {
    setLoading(true);
    setMessage(null);
    try {
      const { error } = await supabase.auth.signInWithOtp({ email });
      if (error) {
        setMessage({ text: "Fehler beim Senden des Codes: " + error.message, tone: "error" });
      } else {
        setSent(true);
        setMessage({ text: `Code wurde an ${email} gesendet.`, tone: "info" });
      }
    } catch (e) {
      setMessage({ text: "Unerwarteter Fehler beim Senden des Codes.", tone: "error" });
      console.warn(e);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(token = code) {
    if (loading) return;
    setLoading(true);
    setMessage(null);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: "email",
      });
      if (error) {
        setMessage({ text: "Ungültiger Code: " + error.message, tone: "error" });
      } else {
        setMessage({ text: "Erfolgreich eingeloggt!", tone: "success" });
        if (onAuthSuccess) onAuthSuccess(data.user);
      }
    } catch (e) {
      setMessage({ text: "Unerwarteter Fehler bei der Verifizierung.", tone: "error" });
      console.warn(e);
    } finally {
      setLoading(false);
    }
  }

  // Zurück zur E-Mail-Eingabe, z. B. bei Tippfehler in der Adresse
  function handleChangeEmail() {
    setSent(false);
    setCode("");
    setMessage(null);
  }

  const canSend = email && !loading;
  const canVerify = code.length === CODE_LENGTH && !loading;

  return (
    <div style={{ maxWidth: 420, margin: "0 auto" }}>
      <ScreenHeader
        center
        title="Willkommen bei fluent!"
        subtitle={
          sent
            ? "Gib den Code aus der E-Mail ein."
            : "Gib deine E-Mail ein, wir schicken dir einen Login-Code."
        }
      />

      {!sent ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSend) handleSendCode();
          }}
        >
          <Stack gap={3}>
            <TextInput
              type="email"
              placeholder="E-Mail-Adresse"
              autoComplete="email"
              aria-label="E-Mail-Adresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" block disabled={!canSend}>
              {loading ? "Sende…" : "Code senden"}
            </Button>
          </Stack>
        </form>
      ) : (
        <Stack gap={3}>
          <CodeInput
            onChange={setCode}
            onComplete={handleVerifyCode}
            disabled={loading}
          />
          <Button block onClick={() => handleVerifyCode()} disabled={!canVerify}>
            {loading ? "Prüfe…" : "Einloggen"}
          </Button>
          <Button block variant="ghost" onClick={handleChangeEmail} disabled={loading}>
            Andere E-Mail verwenden
          </Button>
        </Stack>
      )}

      {message && (
        <Note tone={message.tone} style={{ marginTop: "var(--fluent-space-3)", textAlign: "center" }}>
          {message.text}
        </Note>
      )}
    </div>
  );
}

/**
 * Code-Eingabe mit einem Kästchen pro Ziffer.
 * Erstes Kästchen ist direkt ausgewählt, Tippen springt weiter, Rücktaste zurück,
 * ein eingefügter (oder vom Handy vorgeschlagener) Code verteilt sich auf alle Kästchen.
 * Sobald alle Ziffern da sind, wird onComplete aufgerufen.
 */
function CodeInput({ onChange, onComplete, disabled }) {
  const [digits, setDigits] = useState(() => Array(CODE_LENGTH).fill(""));
  const refs = useRef([]);

  // Beim Öffnen und nach einer Prüfung (Kästchen waren gesperrt) wieder ins Feld springen
  useEffect(() => {
    if (disabled) return;
    const firstEmpty = digits.findIndex((d) => d === "");
    refs.current[firstEmpty === -1 ? CODE_LENGTH - 1 : firstEmpty]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const focusBox = (i) => {
    const box = refs.current[Math.max(0, Math.min(CODE_LENGTH - 1, i))];
    if (box) {
      box.focus();
      box.select();
    }
  };

  const update = (next) => {
    setDigits(next);
    const joined = next.join("");
    onChange(joined);
    if (next.every((d) => d !== "")) onComplete(joined);
  };

  const handleChange = (i, e) => {
    const chars = e.target.value.replace(/\D/g, "");
    const next = [...digits];

    if (!chars) {
      next[i] = "";
      update(next);
      return;
    }

    // In ein gefülltes Kästchen getippt, ohne dass die alte Ziffer markiert war
    // (z. B. iOS): nur die neue Ziffer übernehmen statt beide zu verteilen
    const old = digits[i];
    if (old && chars.length === 2) {
      next[i] = chars[0] === old ? chars[1] : chars[0];
      update(next);
      focusBox(i + 1);
      return;
    }

    // Mehrere Ziffern (Einfügen/Autofill) ab diesem Kästchen verteilen
    chars
      .slice(0, CODE_LENGTH - i)
      .split("")
      .forEach((c, k) => {
        next[i + k] = c;
      });
    update(next);
    focusBox(i + chars.length);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      e.preventDefault();
      const next = [...digits];
      next[i - 1] = "";
      update(next);
      focusBox(i - 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(i + 1);
    }
  };

  return (
    <div className="ui-code">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Ziffer ${i + 1} von ${CODE_LENGTH}`}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className={`ui-code__box${d ? " ui-code__box--filled" : ""}`}
        />
      ))}
    </div>
  );
}
